import { browserSupportsWebAuthn, startAuthentication } from "@simplewebauthn/browser";
import type { PublicKeyCredentialRequestOptionsJSON } from "@simplewebauthn/browser";
import { ArrowRight, Fingerprint, KeyRound, LoaderCircle } from "lucide-react";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";

interface AuthScreenProps {
  configured: boolean;
  passkeysConfigured: boolean;
  onLogin: (password: string) => Promise<void>;
  onAuthenticated: () => void;
}

interface LoginCeremony {
  ceremonyId: string;
  options: PublicKeyCredentialRequestOptionsJSON;
}

function friendlyPasskeyError(reason: unknown): string {
  if (reason instanceof Error && reason.name === "NotAllowedError") return "验证已取消，或这台设备上没有可用的通行密钥。";
  return reason instanceof Error ? reason.message : "无法使用通行密钥。";
}

export function AuthScreen({ configured, passkeysConfigured, onLogin, onAuthenticated }: AuthScreenProps) {
  const supportsPasskeys = useMemo(() => browserSupportsWebAuthn(), []);
  const passkeyAvailable = passkeysConfigured && supportsPasskeys;
  const [ceremony, setCeremony] = useState<LoginCeremony | null>(null);
  const [showRecovery, setShowRecovery] = useState(!passkeyAvailable);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState<"passkey" | "password" | "">("");

  const preparePasskey = useCallback(async () => {
    if (!passkeyAvailable) return;
    try {
      setCeremony(await api.passkeyLoginOptions());
    } catch {
      setCeremony(null);
    }
  }, [passkeyAvailable]);

  useEffect(() => {
    void preparePasskey();
    const timer = passkeyAvailable ? window.setInterval(() => void preparePasskey(), 4 * 60 * 1000) : undefined;
    return () => { if (timer) window.clearInterval(timer); };
  }, [passkeyAvailable, preparePasskey]);

  const usePasskey = async () => {
    if (!ceremony || loading) return;
    setLoading("passkey");
    setError("");
    const active = ceremony;
    setCeremony(null);
    try {
      const response = await startAuthentication({ optionsJSON: active.options });
      await api.verifyPasskeyLogin(active.ceremonyId, response);
      onAuthenticated();
    } catch (reason) {
      setError(friendlyPasskeyError(reason));
      void preparePasskey();
    } finally {
      setLoading("");
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!password || loading || !configured) return;
    setLoading("password");
    setError("");
    try {
      await onLogin(password);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "无法登录。");
    } finally {
      setLoading("");
    }
  };

  return (
    <main className="auth-screen">
      <section className="auth-card" aria-labelledby="auth-title">
        <div className="auth-mark" aria-hidden="true">YK</div>
        <p className="auth-kicker">LIVE KNOWLEDGE READER</p>
        <h1 id="auth-title">阅读 Agent<br />维护的知识。</h1>
        <p className="auth-lede">这里直接连接 Server 当前提交，没有本地副本，也没有编辑入口。</p>
        {configured ? (
          <div className="auth-entry">
            {passkeyAvailable && (
              <button type="button" className="passkey-login-button" onClick={usePasskey} disabled={!ceremony || Boolean(loading)}>
                {loading === "passkey" || !ceremony ? <LoaderCircle className="spin" size={21} /> : <Fingerprint size={22} />}
                <span><strong>使用通行密钥</strong><small>指纹、面容或设备锁屏</small></span>
                <ArrowRight size={19} />
              </button>
            )}
            {passkeyAvailable && (
              <button type="button" className="recovery-toggle" onClick={() => setShowRecovery((value) => !value)}>
                <KeyRound size={14} /> {showRecovery ? "收起恢复密码" : "改用恢复密码"}
              </button>
            )}
            {showRecovery && (
              <form onSubmit={submit} className="auth-form">
                <input className="visually-hidden" type="text" name="username" value="yufo" autoComplete="username" readOnly />
                <label htmlFor="reader-password">恢复密码</label>
                <div className="auth-input-row">
                  <KeyRound size={18} aria-hidden="true" />
                  <input
                    id="reader-password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoFocus={!passkeyAvailable}
                    required
                  />
                  <button type="submit" aria-label="使用恢复密码进入知识库" disabled={Boolean(loading) || !password}>
                    {loading === "password" ? <LoaderCircle className="spin" size={19} /> : <ArrowRight size={19} />}
                  </button>
                </div>
              </form>
            )}
            {error && <p className="auth-error" role="alert">{error}</p>}
            {!supportsPasskeys && passkeysConfigured && <p className="auth-note">当前浏览器不支持通行密钥，请使用恢复密码。</p>}
          </div>
        ) : (
          <div className="auth-pending"><LoaderCircle className="spin" size={18} /> 服务正在完成首次安全配置。</div>
        )}
        <div className="auth-source"><span /> JD-CLOUD · PASSKEY FIRST · READ ONLY</div>
      </section>
    </main>
  );
}
